import React, { useState } from 'react';
import { FlowerButton } from '@/components/ui/FlowerButton';

export const FlowerButtonDemo: React.FC = () => {
  const [state1, setState1] = useState(false);
  const [state2, setState2] = useState(false);
  const [state3, setState3] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Flouri Flower Button
        </h1>
        <p className="text-gray-600 mb-8">
          Click the flower buttons to see them bloom!
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Small size */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Small</h3>
            <FlowerButton
              size={48}
              onToggle={(isOpen) => setState1(isOpen)}
              showCenterDot={true}
            />
            <p className="mt-4 text-sm text-gray-600">
              Status: <span className="font-medium">{state1 ? 'Open' : 'Closed'}</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">With center dot</p>
          </div>

          {/* Medium size */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Medium</h3>
            <FlowerButton
              size={80}
              onToggle={(isOpen) => setState2(isOpen)}
              showCenterDot={true}
            />
            <p className="mt-4 text-sm text-gray-600">
              Status: <span className="font-medium">{state2 ? 'Open' : 'Closed'}</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">With center dot</p>
          </div>

          {/* Large size */}
          <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">Large</h3>
            <FlowerButton
              size={120}
              onToggle={(isOpen) => setState3(isOpen)}
              showCenterDot={false}
            />
            <p className="mt-4 text-sm text-gray-600">
              Status: <span className="font-medium">{state3 ? 'Open' : 'Closed'}</span>
            </p>
            <p className="mt-1 text-xs text-gray-500">Without center dot</p>
          </div>
        </div>

        {/* Usage example */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Usage Example</h2>
          <pre className="bg-gray-100 rounded p-4 overflow-x-auto">
            <code>{`import { FlowerButton } from '@/components/ui/FlowerButton';

function MyComponent() {
  return (
    <FlowerButton
      size={64}
      onToggle={(isOpen) => console.log('Flower is', isOpen ? 'open' : 'closed')}
      defaultOpen={false}
      showCenterDot={true}  // Set to false to hide the center dot
    />
  );
}`}</code>
          </pre>
        </div>

        {/* Props documentation */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Props</h2>
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Prop
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Default
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                  Description
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-2 text-sm font-mono text-gray-900">size</td>
                <td className="px-4 py-2 text-sm text-gray-600">number</td>
                <td className="px-4 py-2 text-sm text-gray-600">64</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  Size of the button in pixels
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm font-mono text-gray-900">onToggle</td>
                <td className="px-4 py-2 text-sm text-gray-600">(isOpen: boolean) =&gt; void</td>
                <td className="px-4 py-2 text-sm text-gray-600">-</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  Callback when button state changes
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm font-mono text-gray-900">defaultOpen</td>
                <td className="px-4 py-2 text-sm text-gray-600">boolean</td>
                <td className="px-4 py-2 text-sm text-gray-600">false</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  Initial state of the flower
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm font-mono text-gray-900">showCenterDot</td>
                <td className="px-4 py-2 text-sm text-gray-600">boolean</td>
                <td className="px-4 py-2 text-sm text-gray-600">true</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  Show/hide the center reference dot
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 text-sm font-mono text-gray-900">className</td>
                <td className="px-4 py-2 text-sm text-gray-600">string</td>
                <td className="px-4 py-2 text-sm text-gray-600">-</td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  Additional CSS classes
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FlowerButtonDemo;
